import { authUtils } from '../utils/auth';
import type {
  RiskAssessmentRequest,
  RiskAssessmentResponse,
  MonteCarloParameters,
  MonteCarloResults,
  StressTestScenario,
  ScenarioImpact,
  PortfolioOptimization,
  RiskAlert,
  EducationalContent
} from '../types/risk-assessment';
import type {
  PortfolioAnalyticsResponse,
  AnalysisCoverageResponse
} from '../types/portfolio';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || 'https://your-api-gateway-url';

// API Client Class
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      console.log(`🔐 API: Starting request to ${endpoint}`);

      // Get JWT token for authentication (ID token for API Gateway Cognito authorizer)
      const token = await authUtils.getIdToken();

      console.log('🎫 API: Token status:', {
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token'
      });

      // If no token available, throw authentication error
      if (!token) {
        console.error('❌ API: No authentication token available');
        throw new Error('Authentication required - no valid token available');
      }

      const url = `${this.baseUrl}${endpoint}`;
      const config: RequestInit = {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        } as Record<string, string>,
      };

      console.log('📤 API: Making HTTP request:', {
        url,
        method: config.method || 'GET',
        hasAuth: !!(config.headers as Record<string, string>)?.['Authorization']
      });

      const response = await fetch(url, config);

      console.log('📥 API: Received response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: {
          contentType: response.headers.get('content-type'),
          contentLength: response.headers.get('content-length')
        }
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          console.log('📋 API: Error response data:', errorData);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If response body can't be parsed as JSON, use status text
          console.warn('⚠️ API: Failed to parse error response as JSON:', parseError);
        }

        console.error(`❌ API: Request failed with error: ${errorMessage}`);
        throw new Error(errorMessage);
      }

      const jsonResponse = await response.json();
      console.log('✅ API: Successfully parsed JSON response for', endpoint);

      return jsonResponse;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;

      console.error(`❌ API: Request failed for ${endpoint}:`, {
        message: errorMessage,
        stack: errorStack,
        endpoint,
        baseUrl: this.baseUrl
      });
      throw error;
    }
  }

  // Investment Analysis Endpoints
  async runInvestmentAnalysis(analysisData: InvestmentAnalysisRequest): Promise<InvestmentAnalysisResponse> {
    return this.makeRequest<InvestmentAnalysisResponse>('/analysis', {
      method: 'POST',
      body: JSON.stringify(analysisData),
    });
  }

  // User Properties Endpoints
  async createUserProperty(propertyData: CreatePropertyRequest): Promise<PropertyResponse> {
    return this.makeRequest<PropertyResponse>('/user-properties', {
      method: 'POST',
      body: JSON.stringify(propertyData),
    });
  }

  async getUserProperties(params?: { limit?: number; status?: string }): Promise<PropertiesListResponse> {
    console.log('🔗 API: getUserProperties called with params:', params);

    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);

    const endpoint = `/user-properties${queryParams.toString() ? `?${queryParams}` : ''}`;

    console.log('📡 API: Making request to endpoint:', endpoint);
    console.log('🌐 API: Full URL:', `${this.baseUrl}${endpoint}`);

    try {
      const response = await this.makeRequest<PropertiesListResponse>(endpoint);

      console.log('✅ API: getUserProperties successful response:', {
        count: response.count,
        propertiesLength: response.properties?.length || 0,
        hasMore: response.has_more,
        sampleProperty: response.properties?.[0] ? {
          id: response.properties[0].property_id,
          address: response.properties[0].property_address,
          price: response.properties[0].purchase_price
        } : null
      });

      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ API: getUserProperties failed:', {
        endpoint,
        error: errorMessage,
        baseUrl: this.baseUrl
      });
      throw error;
    }
  }

  // Portfolio Analytics Endpoints
  async getPortfolioAnalytics(): Promise<PortfolioAnalyticsResponse> {
    console.log('🔗 API: getPortfolioAnalytics called');

    try {
      const response = await this.makeRequest<PortfolioAnalyticsResponse>('/portfolio/analytics');

      console.log('✅ API: getPortfolioAnalytics successful response:', {
        totalProperties: response.data?.total_properties,
        totalAnalyses: response.data?.total_analyses,
        analysisCountByType: response.data?.analysis_count_by_type
      });

      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ API: getPortfolioAnalytics failed:', errorMessage);

      // If endpoint doesn't exist yet, return mock data structure
      if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        console.info('Portfolio analytics endpoint not yet implemented, returning empty structure');
        return {
          success: true,
          data: {
            total_properties: 0,
            total_analyses: 0,
            analysis_count_by_type: {
              'buy-hold': 0,
              'flip': 0,
              'brrr': 0
            },
            properties_with_multiple_analyses: 0,
            average_analyses_per_property: 0,
            recent_activity: {
              properties_added_last_30_days: 0,
              analyses_added_last_30_days: 0
            },
            value_metrics: {
              total_investment: 0,
              total_monthly_cash_flow: 0,
              average_roi: 0,
              average_cap_rate: 0,
              total_equity: 0
            },
            geographic_distribution: {}
          }
        };
      }

      throw error;
    }
  }

  async getAnalysisCoverage(): Promise<AnalysisCoverageResponse> {
    console.log('🔗 API: getAnalysisCoverage called');

    try {
      const response = await this.makeRequest<AnalysisCoverageResponse>('/portfolio/analysis-coverage');

      console.log('✅ API: getAnalysisCoverage successful response:', response);

      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ API: getAnalysisCoverage failed:', errorMessage);

      // If endpoint doesn't exist yet, return mock data structure
      if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        console.info('Analysis coverage endpoint not yet implemented, returning empty structure');
        return {
          success: true,
          data: {
            properties_by_type: {
              'buy-hold': 0,
              'flip': 0,
              'brrr': 0,
              'multiple_types': 0,
              'no_analysis': 0
            },
            analysis_distribution: {
              single_analysis: 0,
              multiple_analyses: 0,
              comprehensive_analysis: 0
            },
            completion_rates: {
              buy_hold_coverage: 0,
              flip_coverage: 0,
              brrr_coverage: 0,
              full_coverage: 0
            }
          }
        };
      }

      throw error;
    }
  }

  async getUserProperty(propertyId: string): Promise<PropertyResponse> {
    return this.makeRequest<PropertyResponse>(`/user-properties/${propertyId}`);
  }

  async updateUserProperty(propertyId: string, propertyData: UpdatePropertyRequest): Promise<PropertyResponse> {
    return this.makeRequest<PropertyResponse>(`/user-properties/${propertyId}`, {
      method: 'PUT',
      body: JSON.stringify(propertyData),
    });
  }

  async deleteUserProperty(propertyId: string): Promise<{ message: string; property_id: string }> {
    return this.makeRequest(`/user-properties/${propertyId}`, {
      method: 'DELETE',
    });
  }

  // Market Assumptions Endpoints
  async getMarketAssumptions(): Promise<MarketAssumptionsResponse> {
    return this.makeRequest<MarketAssumptionsResponse>('/market-assumptions');
  }

  async createMarketAssumptions(assumptions: CreateMarketAssumptionsRequest): Promise<MarketAssumptionsResponse> {
    return this.makeRequest<MarketAssumptionsResponse>('/market-assumptions', {
      method: 'POST',
      body: JSON.stringify(assumptions),
    });
  }

  // Analytics Endpoints
  async trackEvent(eventData: AnalyticsEventData): Promise<AnalyticsResponse> {
    return this.makeRequest<AnalyticsResponse>('/analytics', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  // Tax Analysis Endpoints
  async getTaxProfile(userId: string): Promise<TaxProfileResponse> {
    return this.makeRequest<TaxProfileResponse>(`/tax/profile/${userId}`);
  }

  async updateTaxProfile(profileData: TaxProfileUpdateRequest): Promise<TaxProfileResponse> {
    return this.makeRequest<TaxProfileResponse>('/tax/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async calculateTaxAnalysis(request: TaxAnalysisRequest): Promise<TaxAnalysisResponse> {
    return this.makeRequest<TaxAnalysisResponse>('/tax/analysis', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async generateDepreciationSchedule(request: DepreciationCalculationRequest): Promise<DepreciationScheduleResponse> {
    return this.makeRequest<DepreciationScheduleResponse>('/tax/depreciation', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async calculate1031Exchange(request: Exchange1031Request): Promise<Exchange1031Response> {
    return this.makeRequest<Exchange1031Response>('/tax/1031-exchange', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async generateScheduleE(request: ScheduleERequest): Promise<ScheduleEResponse> {
    return this.makeRequest<ScheduleEResponse>('/tax/schedule-e', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async generateTaxDocuments(request: TaxDocumentRequest): Promise<TaxDocumentResponse> {
    return this.makeRequest<TaxDocumentResponse>('/tax/documents', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getTaxScenarios(propertyId: string): Promise<TaxScenariosResponse> {
    return this.makeRequest<TaxScenariosResponse>(`/tax/scenarios/${propertyId}`);
  }

  async createTaxScenario(request: CreateTaxScenarioRequest): Promise<TaxScenarioResponse> {
    return this.makeRequest<TaxScenarioResponse>('/tax/scenarios', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async updateTaxScenario(scenarioId: string, request: UpdateTaxScenarioRequest): Promise<TaxScenarioResponse> {
    return this.makeRequest<TaxScenarioResponse>(`/tax/scenarios/${scenarioId}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  async deleteTaxScenario(scenarioId: string): Promise<void> {
    return this.makeRequest<void>(`/tax/scenarios/${scenarioId}`, {
      method: 'DELETE',
    });
  }

  // Risk Assessment Endpoints
  async getRiskAssessment(request: RiskAssessmentRequest): Promise<RiskAssessmentResponse> {
    return this.makeRequest<RiskAssessmentResponse>('/risk/assessment', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async runMonteCarloSimulation(params: MonteCarloParameters): Promise<MonteCarloResults> {
    return this.makeRequest<MonteCarloResults>('/risk/monte-carlo', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async runStressTest(scenario: StressTestScenario): Promise<ScenarioImpact> {
    return this.makeRequest<ScenarioImpact>('/risk/stress-test', {
      method: 'POST',
      body: JSON.stringify(scenario),
    });
  }

  async getPortfolioOptimization(portfolioId: string): Promise<PortfolioOptimization> {
    return this.makeRequest<PortfolioOptimization>(`/risk/optimization/${portfolioId}`);
  }

  async getRiskAlerts(portfolioId?: string, options?: {
    severity?: 'low' | 'medium' | 'high' | 'critical';
    type?: 'concentration' | 'leverage' | 'vacancy' | 'market' | 'cash_flow';
    acknowledged?: boolean;
    limit?: number;
  }): Promise<{ alerts: RiskAlert[]; total_count?: number; has_more?: boolean }> {
    let endpoint = portfolioId ? `/risk/alerts/${portfolioId}` : '/risk/alerts';

    // Add query parameters if options provided
    if (options) {
      const queryParams = new URLSearchParams();
      if (options.severity) queryParams.append('severity', options.severity);
      if (options.type) queryParams.append('type', options.type);
      if (options.acknowledged !== undefined) queryParams.append('acknowledged', options.acknowledged.toString());
      if (options.limit) queryParams.append('limit', options.limit.toString());

      if (queryParams.toString()) {
        endpoint += `?${queryParams.toString()}`;
      }
    }

    try {
      const response = await this.makeRequest<{
        alerts: RiskAlert[];
        total_count?: number;
        has_more?: boolean
      }>(endpoint);

      // Validate response structure
      if (!response || !Array.isArray(response.alerts)) {
        console.warn('Invalid alerts response format:', response);
        return { alerts: [] };
      }

      // Validate each alert has required fields
      const validatedAlerts = response.alerts.filter(alert =>
        alert.alert_id &&
        alert.type &&
        alert.severity &&
        alert.title &&
        alert.description
      );

      if (validatedAlerts.length !== response.alerts.length) {
        console.warn('Some alerts were filtered out due to missing required fields');
      }

      return {
        alerts: validatedAlerts,
        total_count: response.total_count,
        has_more: response.has_more
      };
    } catch (error) {
      // Handle different types of errors appropriately
      if (error instanceof Error) {
        // Network or parsing errors
        if (error.message.includes('NetworkError') ||
            error.message.includes('Failed to fetch')) {
          console.warn('Network error when fetching risk alerts:', error.message);
          throw new Error('Network error: Unable to fetch risk alerts. Please check your connection.');
        }

        // 404 - endpoint not implemented yet
        if (error.message.includes('404') || error.message.includes('Not Found')) {
          console.info('Risk alerts endpoint not yet implemented, returning empty alerts');
          return { alerts: [] };
        }

        // 401/403 - authentication/authorization issues
        if (error.message.includes('401') || error.message.includes('403')) {
          console.warn('Authentication error when fetching risk alerts');
          throw new Error('Authentication required: Please log in to view risk alerts.');
        }

        // 500 - server error
        if (error.message.includes('500')) {
          console.error('Server error when fetching risk alerts:', error.message);
          throw new Error('Server error: Unable to process risk alerts request. Please try again later.');
        }
      }

      // Log unexpected errors for debugging but don't break the UI
      console.warn('Unexpected error from risk alerts endpoint:', error);
      return { alerts: [] };
    }
  }

  async acknowledgeRiskAlert(alertId: string, notes?: string): Promise<{ success: boolean; message?: string }> {
    try {
      const body: any = {};
      if (notes) {
        body.acknowledgment_notes = notes;
      }

      const response = await this.makeRequest<{ success: boolean; message?: string }>(`/risk/alerts/${alertId}/acknowledge`, {
        method: 'PUT',
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });

      // Validate response
      if (typeof response.success !== 'boolean') {
        console.warn('Invalid acknowledge response format:', response);
        return { success: false, message: 'Invalid response format' };
      }

      return response;
    } catch (error) {
      if (error instanceof Error) {
        // Network errors
        if (error.message.includes('NetworkError') ||
            error.message.includes('Failed to fetch')) {
          throw new Error('Network error: Unable to acknowledge alert. Please check your connection.');
        }

        // 404 - endpoint or alert not found
        if (error.message.includes('404') || error.message.includes('Not Found')) {
          throw new Error('Alert not found or acknowledgment feature is not yet available');
        }

        // 401/403 - authentication/authorization issues
        if (error.message.includes('401') || error.message.includes('403')) {
          throw new Error('Authentication required: Please log in to acknowledge alerts.');
        }

        // 409 - alert already acknowledged
        if (error.message.includes('409')) {
          throw new Error('Alert has already been acknowledged');
        }

        // 500 - server error
        if (error.message.includes('500')) {
          throw new Error('Server error: Unable to acknowledge alert. Please try again later.');
        }
      }

      console.error('Error acknowledging risk alert:', error);
      throw new Error('Failed to acknowledge alert. Please try again.');
    }
  }

  async dismissRiskAlert(alertId: string, reason?: string): Promise<{ success: boolean }> {
    try {
      const body: any = {};
      if (reason) {
        body.dismissal_reason = reason;
      }

      return await this.makeRequest<{ success: boolean }>(`/risk/alerts/${alertId}/dismiss`, {
        method: 'DELETE',
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });
    } catch (error) {
      if (error instanceof Error && (
        error.message.includes('404') || error.message.includes('Not Found')
      )) {
        throw new Error('Alert dismissal feature is not yet available');
      }
      throw error;
    }
  }

  async getEducationalContent(): Promise<EducationalContent> {
    return this.makeRequest<EducationalContent>('/risk/education');
  }
}

// Types for API requests and responses
export interface InvestmentAnalysisRequest {
  analysis_type: 'buy-hold' | 'flip' | 'brrr';
  property_data: {
    property_address: string;
    city?: string;
    state?: string;
    zip_code?: string;
    purchase_price: number;
    down_payment: number;
    loan_amount: number;
    interest_rate: number;
    loan_term: number;
    annual_taxes: number;
    annual_insurance: number;
    monthly_hoa_fees?: number;
    renovation_costs?: Record<string, number>;
    expected_monthly_rent?: number;
    after_repair_value?: number;
    selling_costs?: number;
    holding_period_months?: number;
  };
  market_assumptions?: {
    vacancy_rate?: number;
    maintenance_rate?: number;
    management_rate?: number;
  };
}

export interface InvestmentAnalysisResponse {
  analysis_id: string;
  analysis_type: string;
  results: {
    analysis_type: string;
    property_metrics?: {
      purchase_price?: number;
      total_cash_invested?: number;
      loan_amount?: number;
      monthly_rent?: number;
      annual_rental_income?: number;
    };
    monthly_analysis?: {
      gross_monthly_income?: number;
      mortgage_payment?: number;
      property_taxes?: number;
      insurance?: number;
      hoa_fees?: number;
      vacancy_allowance?: number;
      maintenance?: number;
      management?: number;
      total_expenses?: number;
      net_cash_flow?: number;
    };
    annual_analysis?: {
      gross_annual_income?: number;
      total_annual_expenses?: number;
      net_operating_income?: number;
      annual_cash_flow?: number;
    };
    investment_returns?: {
      cap_rate?: number;
      cash_on_cash_return?: number;
      gross_rent_multiplier?: number;
      debt_coverage_ratio?: number;
      roi?: number;
      infinite_return?: boolean;
    };
    sensitivity_analysis?: {
      breakeven_rent?: number;
      rent_increase_for_10_percent_coc?: number;
      max_purchase_price_for_8_percent_cap?: number;
    };
    // Future: flip and brrr analysis structures
    project_costs?: any;
    flip_analysis?: any;
    refinance_analysis?: any;
    initial_investment?: any;
  };
  created_at: string;
}

export interface CreatePropertyRequest {
  property_address: string;
  city?: string;
  state?: string;
  zip_code?: string;
  property_type?: string;
  purchase_price: number;
  down_payment: number;
  loan_amount: number;
  interest_rate: number;
  loan_term: number;
  annual_taxes: number;
  annual_insurance: number;
  monthly_hoa_fees?: number;
  renovation_costs?: Record<string, number>;
  expected_monthly_rent?: number;
  vacancy_rate?: number;
  maintenance_rate?: number;
  management_rate?: number;
  after_repair_value?: number;
  holding_period_months?: number;
  selling_costs?: number;
}

export interface UpdatePropertyRequest extends Partial<CreatePropertyRequest> {}

export interface PropertyResponse {
  property_id: string;
  user_id: string;
  property_address: string;
  city?: string;
  state?: string;
  zip_code?: string;
  property_type?: string;
  purchase_price: number;
  down_payment: number;
  loan_amount: number;
  interest_rate: number;
  loan_term: number;
  annual_taxes: number;
  annual_insurance: number;
  monthly_hoa_fees?: number;
  renovation_costs?: Record<string, number>;
  expected_monthly_rent?: number;
  vacancy_rate?: number;
  maintenance_rate?: number;
  management_rate?: number;
  after_repair_value?: number;
  holding_period_months?: number;
  selling_costs?: number;
  analysis_status: string;
  created_at: string;
  updated_at: string;

  // Enhanced analysis metadata (optional for backward compatibility)
  analysis_count?: number;
  last_analysis_date?: string;
  analysis_metadata?: {
    'buy-hold'?: {
      count: number;
      latest: {
        analysis_id: string;
        scenario_name: string;
        created_at: string;
        cash_on_cash_return: number;
      };
    };
    'flip'?: {
      count: number;
      latest: {
        analysis_id: string;
        scenario_name: string;
        created_at: string;
        cash_on_cash_return: number;
      };
    };
    'brrr'?: {
      count: number;
      latest: {
        analysis_id: string;
        scenario_name: string;
        created_at: string;
        cash_on_cash_return: number;
      };
    };
  };
}

export interface PropertiesListResponse {
  properties: PropertyResponse[];
  count: number;
  has_more: boolean;
  last_key?: string;
}

export interface CreateMarketAssumptionsRequest {
  name: string;
  description?: string;
  vacancy_rate: number;
  maintenance_rate: number;
  management_rate: number;
  capital_gains_tax_rate?: number;
  depreciation_rate?: number;
  appreciation_rate?: number;
  is_default?: boolean;
}

export interface MarketAssumptionsResponse {
  assumption_set_id: string;
  user_id: string;
  name: string;
  description?: string;
  vacancy_rate: number;
  maintenance_rate: number;
  management_rate: number;
  capital_gains_tax_rate?: number;
  depreciation_rate?: number;
  appreciation_rate?: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

// Analytics Types
export interface AnalyticsEventData {
  event_type: 'page_view' | 'user_action' | 'button_click' | 'form_submission' | 'error' | 'authentication';
  event_data?: {
    page_path?: string;
    page_title?: string;
    action?: string;
    button_id?: string;
    button_text?: string;
    form_id?: string;
    form_type?: string;
    error_type?: string;
    error_message?: string;
    auth_method?: 'email' | 'google';
    auth_action?: 'login' | 'logout' | 'signup' | 'password_reset';
    property_id?: string;
    analysis_type?: string;
    session_duration?: number;
    referrer?: string;
    user_agent?: string;
    [key: string]: any;
  };
  metadata?: {
    timestamp?: string;
    source?: string;
    version?: string;
    environment?: string;
    [key: string]: any;
  };
}

export interface AnalyticsResponse {
  success: boolean;
  event_id?: string;
  message?: string;
}

// Tax Analysis API Types
export interface TaxProfileResponse {
  success: boolean;
  data: {
    user_id: string;
    filing_status: 'single' | 'married_joint' | 'married_separate' | 'head_of_household';
    federal_tax_bracket: number;
    state: string;
    state_tax_rate: number;
    depreciation_method: 'straight_line' | 'accelerated' | 'bonus';
    material_participation: boolean;
    agi_estimate: number;
    created_at: string;
    updated_at: string;
  };
  error?: string;
}

export interface TaxProfileUpdateRequest {
  user_id: string;
  filing_status?: 'single' | 'married_joint' | 'married_separate' | 'head_of_household';
  federal_tax_bracket?: number;
  state?: string;
  state_tax_rate?: number;
  depreciation_method?: 'straight_line' | 'accelerated' | 'bonus';
  material_participation?: boolean;
  agi_estimate?: number;
}

export interface TaxAnalysisRequest {
  property_id: string;
  analysis_year: string;
  property_data: {
    purchase_price: number;
    annual_rent: number;
    annual_taxes: number;
    annual_insurance: number;
    mortgage_interest: number;
    operating_expenses: number;
    depreciation_basis: number;
  };
  tax_profile: {
    federal_tax_bracket: number;
    state_tax_rate: number;
    filing_status: string;
    material_participation: boolean;
  };
}

export interface TaxAnalysisResponse {
  success: boolean;
  data: {
    property_id: string;
    analysis_year: string;
    rental_income: number;
    total_deductions: number;
    net_rental_income: number;
    taxable_income: number;
    tax_savings: number;
    effective_tax_rate: number;
    after_tax_cash_flow: number;
    depreciation_recapture_liability: number;
    deductions_breakdown: {
      mortgage_interest: number;
      property_taxes: number;
      insurance: number;
      repairs_maintenance: number;
      professional_services: number;
      depreciation: number;
      other_expenses: number;
    };
  };
  error?: string;
}

export interface DepreciationCalculationRequest {
  property_id: string;
  purchase_price: number;
  land_value: number;
  placed_in_service_date: string;
  property_type: 'residential' | 'commercial';
  depreciation_method: 'straight_line' | 'accelerated';
  bonus_depreciation_election?: boolean;
  years_to_calculate?: number;
}

export interface DepreciationScheduleResponse {
  success: boolean;
  data: {
    property_id: string;
    depreciable_basis: number;
    depreciation_method: string;
    useful_life_years: number;
    land_value: number;
    start_date: string;
    annual_schedule: Array<{
      year: number;
      calendar_year: string;
      depreciation_amount: number;
      accumulated_depreciation: number;
      remaining_basis: number;
      bonus_depreciation?: number;
    }>;
    total_depreciation: number;
    remaining_basis: number;
  };
  error?: string;
}

export interface Exchange1031Request {
  property_id: string;
  current_market_value: number;
  original_purchase_price: number;
  total_depreciation_taken: number;
  improvement_costs: number;
  selling_costs_percentage: number;
  tax_profile: {
    federal_tax_bracket: number;
    state_tax_rate: number;
    agi_estimate: number;
  };
  replacement_properties?: Array<{
    address: string;
    market_value: number;
    property_type: string;
  }>;
}

export interface Exchange1031Response {
  success: boolean;
  data: {
    property_id: string;
    current_market_value: number;
    adjusted_basis: number;
    potential_capital_gain: number;
    depreciation_recapture: number;
    federal_capital_gains_rate: number;
    state_capital_gains_rate: number;
    net_investment_income_tax: number;
    total_tax_liability: number;
    tax_deferred_via_1031: number;
    exchange_requirements: {
      identification_period_days: number;
      exchange_period_days: number;
      qualified_intermediary_required: boolean;
      like_kind_requirement: string;
      equal_or_greater_value: boolean;
      debt_replacement_rule: string;
    };
    qualified_replacement_properties: Array<{
      address: string;
      market_value: number;
      qualification_status: 'qualified' | 'not_qualified' | 'pending_review';
      qualification_notes: string;
    }>;
  };
  error?: string;
}

export interface ScheduleERequest {
  property_id: string;
  property_address: string;
  tax_year: string;
  rental_income: number;
  expenses: {
    advertising: number;
    auto_travel: number;
    cleaning_maintenance: number;
    commissions: number;
    insurance: number;
    legal_professional: number;
    management_fees: number;
    mortgage_interest: number;
    other_interest: number;
    repairs: number;
    supplies: number;
    taxes: number;
    utilities: number;
    depreciation: number;
  };
  other_expenses: Array<{
    description: string;
    amount: number;
    category: string;
  }>;
}

export interface ScheduleEResponse {
  success: boolean;
  data: {
    property_id: string;
    property_address: string;
    year: string;
    rental_income: number;
    total_expenses: number;
    net_rental_income: number;
    schedule_e_data: {
      [key: string]: number;
    };
    formatted_exports: {
      csv: string;
      pdf_url?: string;
      turbotax_format: string;
    };
  };
  error?: string;
}

export interface TaxDocumentRequest {
  user_id: string;
  property_ids: string[];
  tax_year: string;
  document_types: ('schedule_e' | 'depreciation_schedule' | 'expense_report' | '1031_worksheet')[];
  export_formats: ('pdf' | 'csv' | 'xlsx' | 'turbotax' | 'taxact')[];
}

export interface TaxDocumentResponse {
  success: boolean;
  data: {
    document_package_id: string;
    generated_at: string;
    documents: Array<{
      document_type: string;
      file_name: string;
      file_format: string;
      download_url: string;
      expires_at: string;
    }>;
    summary: {
      total_rental_income: number;
      total_deductions: number;
      net_rental_income: number;
      total_depreciation: number;
      estimated_tax_savings: number;
    };
  };
  error?: string;
}

export interface TaxScenariosResponse {
  success: boolean;
  data: {
    scenarios: Array<{
      scenario_id: string;
      scenario_name: string;
      property_id: string;
      tax_profile_adjustments: any;
      holding_period_years: number;
      annual_rent_growth: number;
      annual_expense_growth: number;
      exit_strategy: 'sale' | '1031_exchange' | 'hold_indefinitely';
      created_at: string;
      projected_results: Array<{
        year: number;
        rental_income: number;
        total_deductions: number;
        taxable_income: number;
        tax_liability: number;
        after_tax_cash_flow: number;
        cumulative_tax_savings: number;
      }>;
    }>;
  };
  error?: string;
}

export interface CreateTaxScenarioRequest {
  scenario_name: string;
  property_id: string;
  tax_profile_adjustments?: {
    federal_tax_bracket?: number;
    state_tax_rate?: number;
    filing_status?: string;
  };
  holding_period_years: number;
  annual_rent_growth: number;
  annual_expense_growth: number;
  exit_strategy: 'sale' | '1031_exchange' | 'hold_indefinitely';
}

export interface UpdateTaxScenarioRequest {
  scenario_name?: string;
  tax_profile_adjustments?: any;
  holding_period_years?: number;
  annual_rent_growth?: number;
  annual_expense_growth?: number;
  exit_strategy?: 'sale' | '1031_exchange' | 'hold_indefinitely';
}

export interface TaxScenarioResponse {
  success: boolean;
  data: {
    scenario_id: string;
    scenario_name: string;
    property_id: string;
    tax_profile_adjustments: any;
    holding_period_years: number;
    annual_rent_growth: number;
    annual_expense_growth: number;
    exit_strategy: string;
    created_at: string;
    updated_at: string;
    projected_results: Array<{
      year: number;
      rental_income: number;
      total_deductions: number;
      depreciation: number;
      taxable_income: number;
      tax_liability: number;
      after_tax_cash_flow: number;
      cumulative_tax_savings: number;
      property_value_estimate: number;
      cumulative_depreciation: number;
    }>;
  };
  error?: string;
}

// Create and export API client instance
export const apiClient = new ApiClient(API_BASE_URL);
